import { processSessionEventsService } from '@/api/process-session-events.service';
import type { ProcessSessionEventDto, ProcessSessionResponseDto } from '@carvia/ros-client-types';
import type { ServerSentEventService } from '@/api/server-sent-event.service';
import { BOOKINGS_QUERY_KEY, hasPendingProcessSessionPatch } from '@/queries/use-bookings';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, onUnmounted, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

/** Refetch when the push event revision is ahead of what we already have in cache. */
export function isNewerProcessSessionEvent(
  event: ProcessSessionEventDto,
  cached: ProcessSessionResponseDto | null | undefined,
  alwaysRefetchTypes: string[] = ['session.takenOver', 'session.ended', 'session.counterReleased'],
): boolean {
  if (event.type === 'session.heartbeat') {
    return false;
  }
  if (alwaysRefetchTypes.includes(event.type)) {
    return true;
  }
  if (event.type !== 'session.updated') {
    return false;
  }
  return event.revision > (cached?.revision ?? -1);
}

/** Skip refetch for our own in-flight patch echo (onSuccess will apply the response). */
export function shouldRefetchProcessSessionEvent(
  event: ProcessSessionEventDto,
  cached: ProcessSessionResponseDto | null | undefined,
  hasPendingPatch: boolean,
  alwaysRefetchTypes: string[] = ['session.takenOver', 'session.ended', 'session.counterReleased'],
): boolean {
  if (!isNewerProcessSessionEvent(event, cached, alwaysRefetchTypes)) {
    return false;
  }
  if (
    hasPendingPatch &&
    event.type === 'session.updated' &&
    event.revision === (cached?.revision ?? -1) + 1
  ) {
    return false;
  }
  return true;
}

export function useProcessSessionEvents(
  bookingId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
  onTakenOver?: () => void,
  onCounterReleased?: () => void,
) {
  const queryClient = useQueryClient();
  const error = ref<Error | null>(null);
  const isConnected = ref(false);

  let subscription: { unsubscribe: () => void } | null = null;
  let service: ServerSentEventService<ProcessSessionEventDto> | null = null;

  const stop = () => {
    subscription?.unsubscribe();
    subscription = null;
    service?.close();
    service = null;
    isConnected.value = false;
  };

  watch(
    [() => toValue(bookingId), () => toValue(enabled)],
    ([id, isEnabled]) => {
      stop();
      if (!isEnabled || !id) return;

      service = processSessionEventsService(id);
      subscription = service.open().subscribe({
        next: (event) => {
          const cached = queryClient.getQueryData<ProcessSessionResponseDto | null | undefined>([
            'process-session',
            id,
          ]);
          if (
            !shouldRefetchProcessSessionEvent(
              event,
              cached,
              hasPendingProcessSessionPatch(queryClient, id),
            )
          ) {
            return;
          }
          void queryClient.refetchQueries({ queryKey: ['process-session', id] });
          void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY, id] });
          if (event.type === 'session.takenOver') {
            onTakenOver?.();
          }
          if (event.type === 'session.counterReleased') {
            onCounterReleased?.();
          }
        },
        error: (err) => {
          error.value = err instanceof Error ? err : new Error(String(err));
          isConnected.value = false;
        },
        complete: () => {
          isConnected.value = false;
        },
      });
      isConnected.value = true;
    },
    { immediate: true },
  );

  onUnmounted(stop);

  return {
    error,
    isConnected: computed(() => isConnected.value),
  };
}
